import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box, Text, Badge, Group, Stack } from '@mantine/core';
import type { DeviceConfig, PanelConfig } from '../../data/DeviceConfig';
import { isValidIframeUri } from '../../data/DeviceConfig';
import { CLOUD_UI_PLUGIN_ID, COMPONENT_REGISTRY } from './componentRegistry';

import matchImg from '../../assets/match.png';
import draftkingImg from '../../assets/draftking.png';
import marqueeImg from '../../assets/marquee.png';
import betImg from '../../assets/bet.png';

interface ScreenPreviewProps {
    config: DeviceConfig;
}

const CLOUD_PREVIEW_REF_W = 1080;
const CLOUD_PREVIEW_REF_H = 1920;
const IFRAME_SRC_DEBOUNCE_MS = 400;

const CloudUiScaledPreview: React.FC<{ iframeUri: string }> = ({ iframeUri }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [scale, setScale] = useState(1);
    const [iframeSrc, setIframeSrc] = useState<string | null>(null);

    useEffect(() => {
        const handle = window.setTimeout(() => {
            if (isValidIframeUri(iframeUri)) {
                setIframeSrc(iframeUri.trim());
            } else {
                setIframeSrc(null);
            }
        }, IFRAME_SRC_DEBOUNCE_MS);
        return () => window.clearTimeout(handle);
    }, [iframeUri]);

    useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el) {
            return undefined;
        }
        const measure = () => {
            const w = el.clientWidth;
            const h = el.clientHeight;
            setScale(Math.min(w / CLOUD_PREVIEW_REF_W, h / CLOUD_PREVIEW_REF_H));
        };
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    return (
        <Box
            ref={containerRef}
            style={{
                flex: 1,
                minHeight: 0,
                position: 'relative',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <Box
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: CLOUD_PREVIEW_REF_W,
                    height: CLOUD_PREVIEW_REF_H,
                    transform: `scale(${scale})`,
                    transformOrigin: 'top left',
                    pointerEvents: 'none',
                }}
            >
                {iframeSrc ? (
                    <iframe
                        title="Cloud UI preview"
                        src={iframeSrc}
                        style={{
                            width: CLOUD_PREVIEW_REF_W,
                            height: CLOUD_PREVIEW_REF_H,
                            border: 0,
                            display: 'block',
                            background: '#111',
                        }}
                    />
                ) : (
                    <Box
                        style={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '1px dashed rgba(255,255,255,0.12)',
                            borderRadius: 6,
                        }}
                    >
                        <Text style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', padding: 12, textAlign: 'center' }}>
                            Enter a valid http or https URL to preview
                        </Text>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

// Map component names to their thumbnail images
const COMPONENT_THUMBNAILS: Record<string, string> = {
    Match: matchImg,
    Mp4TransitionMatchWidget: matchImg,
    Download: draftkingImg,
    DraftKingsLogoBanner: draftkingImg,
    Marquee: marqueeImg,
    UrlMarquee: marqueeImg,
    Bet: betImg,
};

const TextChip: React.FC<{ component: string; durationSeconds: number }> = ({
    component,
    durationSeconds,
}) => {
    const displayName = COMPONENT_REGISTRY[component]?.label ?? component;
    return (
        <Box
            style={{
                background: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.14)',
                borderRadius: 6,
                padding: '5px 8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 6,
                flexShrink: 0,
            }}
        >
            <Text
                style={{
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: 11,
                    color: 'white',
                    fontWeight: 500,
                }}
            >
                {displayName}
            </Text>
            <Text style={{ whiteSpace: 'nowrap', fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>
                {durationSeconds === 0 ? '∞' : `${durationSeconds}s`}
            </Text>
        </Box>
    );
};

const PanelSection: React.FC<{
    label: string;
    panel: PanelConfig | null;
    flex: number;
}> = ({ label, panel, flex }) => {
    const content = panel?.content ?? [];
    const isCommercialTakeover = panel?.commercialTakeover;
    const firstItem = content[0];
    const thumbnail = firstItem ? COMPONENT_THUMBNAILS[firstItem.component] : undefined;

    return (
        <Box
            style={{
                flex,
                borderBottom: '1px solid rgba(255,255,255,0.08)',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                overflow: 'hidden',
                position: 'relative',
            }}
        >
            {content.length === 0 ? (
                // Empty state
                <>
                    <PanelLabel label={label} isCommercialTakeover={!!isCommercialTakeover} />
                    <Box
                        style={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '4px 8px 6px',
                            border: '1px dashed rgba(255,255,255,0.12)',
                            borderRadius: 6,
                        }}
                    >
                        <Text style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)' }}>Empty</Text>
                    </Box>
                </>
            ) : thumbnail ? (
                // Image-backed: fill the section with the thumbnail
                <Box style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
                    <img
                        src={thumbnail}
                        alt={firstItem.component}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                    {/* Top-left: panel label */}
                    <Box
                        style={{
                            position: 'absolute',
                            top: 4,
                            left: 4,
                            background: 'rgba(0,0,0,0.55)',
                            borderRadius: 3,
                            padding: '2px 5px',
                        }}
                    >
                        <Group gap={4}>
                            <Text
                                style={{
                                    fontSize: 7,
                                    color: 'rgba(255,255,255,0.55)',
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.1em',
                                }}
                            >
                                {label}
                            </Text>
                            {isCommercialTakeover && (
                                <Badge
                                    size="xs"
                                    color="yellow"
                                    variant="light"
                                    style={{ fontSize: 7, padding: '0 4px' }}
                                >
                                    ADV
                                </Badge>
                            )}
                        </Group>
                    </Box>
                    {/* Bottom-right: duration */}
                    <Box
                        style={{
                            position: 'absolute',
                            bottom: 4,
                            right: 4,
                            background: 'rgba(0,0,0,0.55)',
                            borderRadius: 3,
                            padding: '2px 5px',
                        }}
                    >
                        <Text style={{ fontSize: 9, color: 'rgba(255,255,255,0.7)' }}>
                            {firstItem.durationSeconds === 0 ? '∞' : `${firstItem.durationSeconds}s`}
                        </Text>
                    </Box>
                    {/* Bottom-left: additional items indicator */}
                    {content.length > 1 && (
                        <Box
                            style={{
                                position: 'absolute',
                                bottom: 4,
                                left: 4,
                                background: 'rgba(0,0,0,0.55)',
                                borderRadius: 3,
                                padding: '2px 5px',
                            }}
                        >
                            <Text style={{ fontSize: 9, color: 'rgba(255,255,255,0.5)' }}>
                                +{content.length - 1} more
                            </Text>
                        </Box>
                    )}
                </Box>
            ) : (
                // Text-chip layout for components without a thumbnail
                <Box style={{ display: 'flex', flexDirection: 'column', padding: '6px 8px', gap: 4, flex: 1 }}>
                    <PanelLabel label={label} isCommercialTakeover={!!isCommercialTakeover} />
                    <Stack gap={3} style={{ flex: 1, overflow: 'hidden', justifyContent: 'center' }}>
                        {content.slice(0, 3).map((item, i) => (
                            <TextChip key={i} component={item.component} durationSeconds={item.durationSeconds} />
                        ))}
                        {content.length > 3 && (
                            <Text style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', textAlign: 'center' }}>
                                +{content.length - 3} more
                            </Text>
                        )}
                    </Stack>
                </Box>
            )}
        </Box>
    );
};

// Extracted so it can be used in both empty and text-chip states
const PanelLabel: React.FC<{ label: string; isCommercialTakeover: boolean }> = ({
    label,
    isCommercialTakeover,
}) => (
    <Group gap={4} style={{ flexShrink: 0, padding: '4px 8px 0' }}>
        <Text
            style={{
                fontSize: 8,
                color: 'rgba(255,255,255,0.35)',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
            }}
        >
            {label}
        </Text>
        {isCommercialTakeover && (
            <Badge size="xs" color="yellow" variant="light" style={{ fontSize: 7, padding: '0 4px' }}>
                ADV
            </Badge>
        )}
    </Group>
);

const ScreenPreview: React.FC<ScreenPreviewProps> = ({ config }) => {
    return (
        <Box style={{ position: 'sticky', top: 16 }}>
            <Text size="sm" fw={500} c="dimmed" mb="xs">
                Screen Preview
            </Text>

            {/* Outer bezel — mimics a vertical kiosk/signage screen */}
            <Box
                style={{
                    background: 'linear-gradient(160deg, #2a2a2a 0%, #1a1a1a 100%)',
                    borderRadius: 6,
                    padding: '6px',
                    boxShadow:
                        '0 16px 48px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.08), inset 0 0 0 1px rgba(255,255,255,0.05)',
                }}
            >
                {/* Screen */}
                <Box
                    style={{
                        background: '#080808',
                        borderRadius: 4,
                        overflow: 'hidden',
                        aspectRatio: '9 / 16',
                        display: 'flex',
                        flexDirection: 'column',
                        boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.06)',
                    }}
                >
                    {config.pluginId === CLOUD_UI_PLUGIN_ID ? (
                        <CloudUiScaledPreview iframeUri={config.iframeUri ?? ''} />
                    ) : (
                        <>
                            <PanelSection label="Top Panel" panel={config.topPanel} flex={2} />
                            <PanelSection label="Middle Panel" panel={config.middlePanel} flex={6} />
                            <PanelSection label="Bottom Panel" panel={config.bottomPanel} flex={2} />
                        </>
                    )}
                </Box>

            </Box>

            {config.pluginId && (
                <Text size="xs" c="dimmed" ta="center" mt="xs">
                    {config.pluginId}
                </Text>
            )}
        </Box>
    );
};

export default ScreenPreview;
